import React from 'react';

import {includes, get, isEmpty} from 'lodash-es';
import PropTypes from 'prop-types';

import {onSuggestionKeyDown} from 'static/three-oh/src/components/GlobalLayout/GlobalNav/GlobalNavSearch/helpers';
import {
  GlobalNavSearchSuggestion,
} from 'static/three-oh/src/components/GlobalLayout/GlobalNav/GlobalNavSearch/SearchSuggestions/SuggestionParts/SuggestionParts';
import {gradesFormatter} from 'static/three-oh/utils/gradeFormatting.js';

import styles from './styles';

export default class Suggestion extends React.Component {
  static propTypes = {
    focusNext: PropTypes.func.isRequired,
    focusPrevious: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    searchId: PropTypes.string,
    suggestion: PropTypes.object.isRequired,
    tabIndex: PropTypes.number.isRequired,
    isGlobalNavSearch: PropTypes.bool.isRequired,
    onSuggestionDismiss: PropTypes.func,
  }

  static defaultProps = {
    isGlobalNavSearch: false,
  }

  state = {
    isSelected: false,
  };

  /**
   * Handle events where the suggestion loses focus.
   * @param {Event} event - the event.
   */
  onBlur = () => {
    this.setState({isSelected: false});
  }

  /**
   * Handle suggestion clicks by triggering a callback function.
   */
  onClick = (click = true) => {
    const {suggestion, searchId, onClick} = this.props;
    /**
     * First we grab the selected standard suggestion object...
     * We sequester and create a deep copy of the suggestion object
     * to create our event properties object, which we will also
     * add a search_id to
     */
    const eventProperties = {...suggestion, search_id: searchId};

    /**
     * Next we pass the standardSuggestion object, eventProperties, click
     * to the onclick callback
     */
    onClick(suggestion, eventProperties, click);
  }

  /**
   * Handle events where the suggestion gains focus.
   * @param {Event} event - the event.
   */
  onFocus = () => {
    const {suggestion, onFocus} = this.props;

    onFocus(suggestion);
    this.setState({isSelected: true});
  }

  /**
   * Handle keydown events.
   * @param {Event} event - the keyboard event.
   */
  onKeyDown = (event) => {
    const {tabIndex, focusNext, focusPrevious} = this.props;
    if (includes(['Enter', ' '], event.key)) {
      this.onClick(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusNext(tabIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusPrevious(tabIndex);
    }
  };

  onKeyDownForGlobalNavSearchSuggestion = (event) => {
    const params = {
      event: event,
      onSuggestionSubmit: () => this.onClick(false),
      onSuggestionDismiss: this.props.onSuggestionDismiss,
      onSuggestionTab: this.props.onSuggestionDismiss,
    };
    onSuggestionKeyDown(params);
  };

  render() {
    const {suggestion, tabIndex, isGlobalNavSearch} = this.props;
    const suggestionTitle = get(suggestion, 'display_title', '');
    const suggestionText = get(suggestion, 'display_text', '');
    const suggestionRegions = get(suggestion, 'regions', '');
    const suggestionGrades = get(suggestion, 'grades', '');
    /** Filter out 'US' from all region arrays */
    const regionString = !isEmpty(suggestionRegions) ? suggestionRegions.filter((region) => region !== 'US') : '';
    const gradesFormatted = gradesFormatter(suggestionGrades);
    const gradesString = !isEmpty(suggestionGrades) ? gradesFormatted : '';

    return (
      isGlobalNavSearch ?
          (
            <GlobalNavSearchSuggestion
              isSelected={this.state.isSelected}
              onClick={this.onClick}
              onKeyDown={this.onKeyDownForGlobalNavSearchSuggestion}
              suggestionText={suggestionText}
              standardTitle={suggestionTitle}
              grade={gradesString}
              region={regionString}
              isStandard
            />
          ) :
          (
            <li
              aria-selected={this.state.isSelected}
              className={`search-suggestion ${styles.suggestion}`}
              data-qa-selector="search_suggestion"
              onBlur={this.onBlur}
              onClick={this.onClick}
              onFocus={this.onFocus}
              onKeyDown={this.onKeyDown}
              role="option"
              tabIndex={tabIndex}
            >
              <div className={styles.descriptionRow}>
                <div className={styles.title}>{suggestionTitle}</div>
                <div className={styles.grade}>
                  {`${gradesString} (${regionString})`}
                </div>
              </div>
              <div className={styles.text}>{suggestionText}</div>
            </li>
          )
    );
  }
}
